import { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import { getExternalIdNameCollection, ProductFromGetMany, useProducts } from '@/models/product';
import { useDatabaseTranslation } from '@/services/i18n';
import { useBrandOrAppRequestContext } from '@/utils/useBrandOrAppRequestContext';
import useDebouncedState from '@/utils/useDebouncedState';

import { ProductSearchSelectReference } from './ProductSearchSelectReference';

type ProductOption = {
  id: string;
  externalId: string;
};

const ProductSearchSelect = ({
  article,
  isReplacementProduct = false,
  showLabel = true,
  label,
  showOptionalLabel = false,
  placeholder,
  value,
  onChange,
  isDisabled,
  variant = 'default',
  error,
  focusOnRender,
  noOptionsMessage,
  onCreateProduct,
}: {
  article?: {
    brandId: string | null;
  };
  isReplacementProduct?: boolean;
  showLabel?: boolean;
  label?: string;
  showOptionalLabel?: boolean;
  placeholder?: string;
  value: ProductFromGetMany | undefined | null;
  onChange: (product: ProductFromGetMany | undefined | null) => void;
  isDisabled?: boolean;
  variant?: 'default' | 'brand';
  error?: string;
  focusOnRender?: boolean;
  noOptionsMessage?: ReactNode;
  onCreateProduct?: (externalId: string) => void;
}) => {
  const { t } = useLingui();
  const { workflow } = useBrandOrAppRequestContext();
  const { _db } = useDatabaseTranslation();

  const customWording =
    workflow?.config?.fields?.request?.articles?.details?.itemReference?.customWording;

  const [query, debouncedQuery, setQuery] = useDebouncedState<string>(
    value ? getOptionText(value) : '',
    500
  );

  const addProductOption: ProductOption = {
    id: 'add',
    externalId: '',
  };

  const { data: { products } = { products: [] }, isFetching } = useProducts(
    {
      ...(isReplacementProduct
        ? { brandIdToReplace: article?.brandId ?? undefined }
        : { brandId: article?.brandId ?? undefined }),
      query: debouncedQuery || undefined,
      limit: 10,
    },
    {
      keepPreviousData: true,
      enabled: !isDisabled,
    }
  );

  const productsWithImages = products.find((product) => !!product.data.image);

  const showAddProductOption =
    !!onCreateProduct && !products.length && !isFetching && query === debouncedQuery;

  const productsAndAddOptions = [
    ...products,
    showAddProductOption ? addProductOption : undefined,
  ].filter((option) => option !== undefined);

  const handleChange = (productOption: ProductFromGetMany | null) => {
    if (!!onCreateProduct && productOption?.id === 'add') {
      if (query !== '') {
        onCreateProduct(query);
      }
    } else {
      onChange(productOption);
    }

    setQuery('');
  };

  return (
    <Stack gap="0.25rem" style={{ flex: 1, maxWidth: '100%' }}>
      <Stack row mobileColumn gap="1rem">
        <div style={{ flex: 1, maxWidth: '100%' }}>
          <InputSearchSelect
            noOptionsMessage={noOptionsMessage ? () => noOptionsMessage : undefined}
            variant="search"
            label={
              showLabel ? (
                <p>
                  {label ?? <Trans id="article.form.article-reference.label">Item reference</Trans>}
                  {!!customWording && <> ({_db(customWording)})</>}
                  {showOptionalLabel && <OptionalLabel />}
                </p>
              ) : undefined
            }
            aria-label={
              !showLabel
                ? t({
                    id: 'article.form.article-reference.aria-label',
                    message: 'Enter your item reference number or the name of the item',
                  })
                : undefined
            }
            styleVariant={variant}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={focusOnRender}
            placeholder={
              placeholder ??
              t({
                id: 'article.form.article-reference.placeholder',
                message: 'Search by item reference or name',
              })
            }
            isDisabled={isDisabled}
            value={value}
            formatOptionLabel={(option, { context }) => {
              if (option?.id === 'add') {
                return (
                  <Stack gap="0.25rem" row>
                    <Trans id="article.form.article-reference.no-results">No results found.</Trans>

                    {query !== '' && (
                      <span style={{ textDecoration: 'underline' }}>
                        <Trans id="article.form.article-reference.add">
                          Add "{query}" as a new product reference
                        </Trans>
                      </span>
                    )}
                  </Stack>
                );
              } else {
                return context === 'menu' ? (
                  <ProductSearchSelectReference product={option} image={!!productsWithImages} />
                ) : (
                  getOptionText(option)
                );
              }
            }}
            options={productsAndAddOptions as ProductFromGetMany[]}
            getOptionValue={getOptionText}
            getOptionLabel={getOptionText}
            inputValue={query}
            onInputChange={setQuery}
            blurInputOnSelect
            filterOption={null} // Avoid react-select to filter options only based on their label
            onChange={handleChange}
            isLoading={isFetching || query !== debouncedQuery}
            error={error}
          />
        </div>
      </Stack>
    </Stack>
  );
};

const getOptionText = (product: ProductFromGetMany) => getExternalIdNameCollection(product);

export default ProductSearchSelect;
